import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Tabs, Radio, Checkbox } from 'antd';
import { AppstoreOutlined, BarsOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import type { ForgeQuery } from '../../../components/ForgeQueryBuilder';
import PageList, { PageListRef } from '../../../components/Templates/PageList';
import OrderBlockShow from './OrderBlockShow';
import OrderSceneTable, { ShowField } from './OrderSceneTable';
import { MergedOrder, OrderTabScene } from '../../../types/process/order';
import { getOrderSubStatusTag } from '../order.effect';
import { FieldConfig } from '../../../components/ForgeQueryBuilder/types';
import { orderApi, type StatsResponseItem } from '../../../services/process/order';
import { buildOrderListInitialState } from './effect';

export interface ViewModeConfig {
  mode: 'list' | 'table';
  label: string;
  showField?: ShowField;
  rowSelection?: any;
  renderActions?: (order: MergedOrder, context?: OrderListActionContext) => React.ReactNode;
  renderInfo?: (order: MergedOrder, context?: OrderListActionContext) => React.ReactNode;
}

export interface OrderListActionContext {
  scene?: string;
  sceneStat?: StatsResponseItem;
}

export interface TabStyleConfig {
  importantTabs?: string[]; // keys of scenes that should highlight their numbers
}

export interface OrderListTemplateProps {
  /** 查询字段配置 */
  queryFields: FieldConfig[];
  /** KPI 统计场景 */
  kpiScene?: string;
  /** Tab 统计场景 */
  tabScene?: OrderTabScene;
  defaultScene?: string;
  /** 视图模式配置 */
  viewModes?: ViewModeConfig[];
  /** 标题 */
  title?: string;
  /** 触发刷新 */
  refreshKey?: number;
  /** 批量操作区域 (位于右侧，视图切换按钮旁边) */
  batchActionNode?: React.ReactNode;
  /** Tab 样式配置 */
  tabStyleConfig?: TabStyleConfig;
}

const OrderListHeader: React.FC<{ rowSelection?: any; rows?: MergedOrder[] }> = ({ rowSelection, rows = [] }) => {
  const isAllSelected = rows.length > 0 && rows.every(r => rowSelection?.selectedRowKeys?.includes(r.order.orderNo));
  const isIndeterminate = rows.length > 0 && rows.some(r => rowSelection?.selectedRowKeys?.includes(r.order.orderNo)) && !isAllSelected;

  const onCheckAllChange = (e: any) => {
    if (!rowSelection?.onChange) return;
    if (e.target.checked) {
      const newKeys = Array.from(new Set([...(rowSelection.selectedRowKeys || []), ...rows.map(r => r.order.orderNo)]));
      const newRowsMap = new Map((rowSelection.selectedRows || []).map((r: any) => [r.order.orderNo, r]));
      rows.forEach(r => newRowsMap.set(r.order.orderNo, r));
      rowSelection.onChange(newKeys, Array.from(newRowsMap.values()));
    } else {
      const keysToRemove = new Set(rows.map(r => r.order.orderNo));
      const newKeys = (rowSelection.selectedRowKeys || []).filter((k: any) => !keysToRemove.has(k));
      const newRows = (rowSelection.selectedRows || []).filter((r: any) => !keysToRemove.has(r.order.orderNo));
      rowSelection.onChange(newKeys, newRows);
    }
  };

  return (
    <div style={{
      display: 'flex',
      background: '#e6f7ff',
      padding: '12px 0',
      fontWeight: 'bold',
      borderRadius: '4px 4px 0 0',
      marginBottom: 0,
      borderBottom: '1px solid #f0f0f0',
      alignItems: 'center',
    }}>
      {rowSelection && (
        <div style={{ paddingLeft: 16, width: 32 }}>
          <Checkbox indeterminate={isIndeterminate} checked={isAllSelected} onChange={onCheckAllChange} />
        </div>
      )}
      <div style={{ flex: 2.5, paddingLeft: rowSelection ? 8 : 16 }}>商品信息</div>
      <div style={{ flex: 2, paddingLeft: 16 }}>押金/租金/数量</div>
      <div style={{ flex: 2, paddingLeft: 16 }}>实付款/租金</div>
      <div style={{ flex: 2, paddingLeft: 16 }}>用户信息</div>
      <div style={{ flex: 1.5, textAlign: 'center' }}>状态</div>
      <div style={{ flex: 1, textAlign: 'center' }}>操作</div>
    </div>
  );
};

const OrderListTemplate: React.FC<OrderListTemplateProps> = ({
  queryFields,
  kpiScene,
  tabScene,
  defaultScene,
  viewModes = [{ mode: 'list', label: '列表' }],
  refreshKey,
  batchActionNode,
  tabStyleConfig,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [initialSearch] = useState(location.search);

  // 初始化解析：URL 参数 + 传入默认场景
  const initialState = useMemo(() => buildOrderListInitialState({
    search: initialSearch,
    defaultScene,
    queryFields,
    tabScene
  }), [initialSearch, defaultScene, queryFields, tabScene]);

  const effectiveQueryFields = queryFields;
  const listRef = useRef<PageListRef>(null);
  const currentSceneRef = useRef<string | undefined>(initialState.initialSceneKey);
  const lastRefreshKeyRef = useRef<number | undefined>(refreshKey);
  const hasAppliedForcedTabRef = useRef(false);
  const [initFinished, setInitFinished] = useState(!tabScene);

  // Stats State
  const [dashboardStats, setDashboardStats] = useState<StatsResponseItem[]>([]);
  const [tabStats, setTabStats] = useState<StatsResponseItem[]>([]);
  const [activeTab, setActiveTab] = useState<string>('0');

  // View Mode
  const initialViewMode = useMemo<'list' | 'table'>(() => {
    const matched = viewModes.find(v => v.mode === initialState.forcedViewMode);
    return (matched?.mode || viewModes[0].mode) as 'list' | 'table';
  }, [initialState.forcedViewMode, viewModes]);
  const [currentViewMode, setCurrentViewMode] = useState<'list' | 'table'>(initialViewMode);

  const currentActionContext: OrderListActionContext = {
    scene: currentSceneRef.current,
    sceneStat: tabStats[Number(activeTab)],
  };

  /** 刷新统计并同步场景，必要时触发列表重拉 */
  const refreshStats = async (query: ForgeQuery = {}) => {
    try {
      if (!kpiScene && !tabScene) {
        setDashboardStats([]);
        setTabStats([]);
        return;
      }

      const statsQuery = { ...query };
      const [kpis, tabs] = await Promise.all([
        kpiScene ? orderApi.getStats(kpiScene, statsQuery as any) : Promise.resolve([]),
        tabScene ? orderApi.getStats(tabScene, statsQuery as any) : Promise.resolve([]),
      ]);

      setDashboardStats(kpiScene ? (kpis || []) : []);
      setTabStats(tabScene ? (tabs || []) : []);

      if (!tabScene) {
        currentSceneRef.current = defaultScene;
        setActiveTab('0');
        return;
      }

      if (!tabs || tabs.length === 0) {
        if (!currentSceneRef.current) currentSceneRef.current = defaultScene;
        setActiveTab('0');
        return;
      }

      const prevScene = currentSceneRef.current;
      const desiredScene = prevScene || defaultScene;
      let nextIndex = 0;
      if (initialState.forcedTabKey && !hasAppliedForcedTabRef.current) {
        const forcedIndex = tabs.findIndex(t => t.key === initialState.forcedTabKey);
        hasAppliedForcedTabRef.current = true;
        if (forcedIndex >= 0) {
          nextIndex = forcedIndex;
        }
      } else if (desiredScene) {
        const found = tabs.findIndex(t => t.key === desiredScene);
        if (found >= 0) nextIndex = found;
      }
      const nextScene = tabs[nextIndex]?.key || desiredScene;
      currentSceneRef.current = nextScene || prevScene || defaultScene;
      setActiveTab(String(nextIndex));

      if (nextScene && nextScene !== prevScene && listRef.current) {
        const q = listRef.current.getQuery() || {};
        listRef.current.search(q);
      }
    } catch (e) { console.error(e); }
  };

  // stringify initialQuery to avoid unnecessary effect triggers
  const initialQueryStr = useMemo(() => JSON.stringify(initialState.initialQuery), [initialState.initialQuery]);

  // Initial load
  useEffect(() => {
    if (!tabScene) {
      setInitFinished(true);
      return;
    }
    const initializeStats = async () => {
      await Promise.all([
        refreshStats({ filter: initialState.initialQuery.filter }),
      ]);
      setInitFinished(true);
    };
    initializeStats();
  }, [tabScene, initialState.initialQuery.filter]); // Removed defaultScene logic completely as it causes weird reload bugs when toggling between tabs sharing same component

  useEffect(() => {
    if (!initFinished) return;
    if (refreshKey === undefined) return;
    if (lastRefreshKeyRef.current === refreshKey) return;
    lastRefreshKeyRef.current = refreshKey;
    const q = listRef.current?.getQuery() || {};
    if (listRef.current) {
      listRef.current.reload();
    } else {
      refreshStats(q);
    }
  }, [refreshKey]);

  useEffect(() => {
    if (viewModes.some(v => v.mode === currentViewMode)) return;
    setCurrentViewMode(initialViewMode);
  }, [currentViewMode, initialViewMode, viewModes]);

  // Tab Change for Stats
  const handleInternalTabChange = (key: string) => {
    setActiveTab(key);
    const index = Number(key);
    const stat = tabStats[index];

    if (stat) {
      currentSceneRef.current = stat.key;
      if (listRef.current) {
        listRef.current.search(listRef.current.getQuery());
      }
    }
  };

  const handleExport = async (query: ForgeQuery) => {
    const mergedParams: any = { ...query };
    const activeScene = currentSceneRef.current;
    if (activeScene) {
      mergedParams.scene = activeScene;
    }

    try {
      await orderApi.export(mergedParams);
      message.success('导出任务已提交，请前往我的导出查看导出列表');
    } catch (e: any) {
      const errorMsg = e.response?.data?.message || e.message || '导出失败';
      message.error(errorMsg);
    }
  };

  const renderRadioGroup = () => (
    viewModes.length > 1 ? (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Radio.Group
          value={currentViewMode}
          onChange={e => setCurrentViewMode(e.target.value)}
          buttonStyle="solid"
          size="small"
        >
          {viewModes.map(vm => (
            <Radio.Button key={vm.mode} value={vm.mode}>
              {vm.mode === 'table' ? <AppstoreOutlined /> : <BarsOutlined />} {vm.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>
    ) : null
  );

  if (!initFinished) return null;
  const pageListKey = `${kpiScene || ''}-${tabScene || ''}`;
  return (
    <PageList<MergedOrder>
      key={pageListKey}
      ref={listRef}
      queryFields={effectiveQueryFields}
      layoutConfig={{ pagination: 'fixed' }}
      ignoreRemembered={!!initialState.forcedQuery}
      syncToUrl={{
        extraParams: {
          t: currentSceneRef.current,
          v: currentViewMode === 'list' ? undefined : currentViewMode
        }
      }}
      defaultQuery={initialState.initialQuery}
      onExport={handleExport}
      fetcher={async (params: any) => {
        const mergedParams = { ...params };
        const activeScene = currentSceneRef.current;
        if (activeScene) {
          mergedParams.scene = activeScene;
        }

        refreshStats({ filter: mergedParams.filter, sort: mergedParams.sort });
        const res = await orderApi.list(mergedParams);
        return {
          rows: res.data.items || [],
          pagination: res.data.pagination
        };
      }}
      buildParams={(q) => q as any}
      kpiItems={dashboardStats.map(s => ({
        title: s.label,
        value: s.value,
      }))}
      headerExtra={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          {tabStats.length > 0 ? (
            <Tabs
              activeKey={activeTab}
              onChange={handleInternalTabChange}
              items={tabStats.map((s, index) => {
                const isImportant = tabStyleConfig?.importantTabs?.includes(s.key);
                const hasNumber = s.value > 0;
                let labelNode: React.ReactNode = `${s.label} (${s.value})`;

                if (isImportant && hasNumber) {
                  labelNode = (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {s.label}
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#ff4d4f',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '0 6px',
                        fontSize: 12,
                        height: 20,
                        lineHeight: '20px'
                      }}>
                        {s.value}
                      </span>
                    </span>
                  );
                }

                return {
                  label: labelNode,
                  key: String(index)
                };
              })}
              style={{ flex: 1 }}
              tabBarExtraContent={
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  {batchActionNode}
                  {renderRadioGroup()}
                </div>
              }
            />
          ) : (
            <>
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                {batchActionNode}
                {renderRadioGroup()}
              </div>
            </>
          )}
        </div>
      }
      renderList={({ rows }) => {
        const viewConfig = viewModes.find(v => v.mode === currentViewMode);
        const actions = viewConfig?.renderActions;

        if (currentViewMode === 'table') {
          return (
            <OrderSceneTable
              dataSource={rows}
              showField={viewConfig?.showField}
              activeScene={currentSceneRef.current}
              rowSelection={viewConfig?.rowSelection}
              renderActions={
                actions
                  ? (order) => actions(order, currentActionContext)
                  : undefined
              }
            />
          );
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <OrderListHeader rowSelection={viewConfig?.rowSelection} rows={rows} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
              {rows.map((order) => (
                <OrderBlockShow
                  key={order.order.orderNo}
                  order={order}
                  rowSelection={viewConfig?.rowSelection}
                  renderActions={
                    actions
                      ? (o) => actions(o, currentActionContext)
                      : undefined
                  }
                  renderInfo={
                    viewConfig?.renderInfo
                      ? (o) => viewConfig.renderInfo!(o, currentActionContext)
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
        );
      }}
    />
  );
};

export default OrderListTemplate;
